import React from 'react'
import { 
  Package, 
  TrendingDown, 
  Plus, 
  X, 
  Truck,
  DollarSign,
  AlertTriangle,
  CheckCircle2,
  Package2,
  Building2,
  Lightbulb,
  TrendingUp,
  Shield,
  BarChart3,
  Target
} from 'lucide-react'
import { useOnboardingStore } from '../../../stores/onboardingStore'
import { getIndustryConfig } from '../industry-configs'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/Card'
import { Input } from '../../../components/ui/Input'
import { Label } from '../../../components/ui/Label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/Select'
import { Textarea } from '../../../components/ui/Textarea'
import { Button } from '../../../components/ui/Button'
import { HelpTooltip } from '../../../components/ui/Tooltip'
import { SupplierConfig } from '../../../types'
import { cn } from '../../../lib/utils'

export const SupplyChainIntelligenceScreen: React.FC = () => {
  const { data, updateData } = useOnboardingStore()
  
  if (!data.selectedIndustry) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-700 mb-2">Industry Selection Required</h3>
          <p className="text-gray-500">Please select an industry first to configure your supply chain intelligence.</p>
        </div>
      </div>
    )
  }
  
  const industryConfig = getIndustryConfig(data.selectedIndustry)

  const addSupplierCategory = (categoryId: string) => {
    const category = industryConfig.supplierCategories.find(c => c.id === categoryId)
    if (!category) return

    const existingSupplier = data.suppliers.find(s => s.category === categoryId)
    if (!existingSupplier) {
      const newSupplier: SupplierConfig = {
        category: categoryId,
        primarySupplier: '',
        monthlySpend: '',
        alternativeSuppliers: []
      }
      updateData('suppliers', [...data.suppliers, newSupplier])
    }
  }

  const updateSupplier = (categoryId: string, field: keyof SupplierConfig, value: any) => {
    const updatedSuppliers = data.suppliers.map(supplier => 
      supplier.category === categoryId ? { ...supplier, [field]: value } : supplier
    )
    updateData('suppliers', updatedSuppliers)
  }

  const removeSupplierCategory = (categoryId: string) => {
    const filteredSuppliers = data.suppliers.filter(supplier => supplier.category !== categoryId)
    updateData('suppliers', filteredSuppliers)
  }

  // Calculate completion status
  const getCompletionStats = () => {
    const configuredSuppliers = data.suppliers.filter(s => s.primarySupplier && s.monthlySpend)
    const totalCategories = industryConfig.supplierCategories.length
    const configuredCategories = data.suppliers.length
    const completedConfigurations = configuredSuppliers.length
    
    return {
      totalCategories,
      configuredCategories,
      completedConfigurations,
      completionPercentage: Math.round((completedConfigurations / Math.max(configuredCategories, 1)) * 100)
    }
  }

  const stats = getCompletionStats()

  // Get category icon based on name
  const getCategoryIcon = (categoryName: string) => {
    if (categoryName.toLowerCase().includes('material')) return Package2
    if (categoryName.toLowerCase().includes('tool')) return Building2
    if (categoryName.toLowerCase().includes('part')) return Package
    return Truck
  }

  return (
    <div className="space-y-8">
      {/* Header Section */}
      <div className="text-center mb-12">
        <div className="inline-flex items-center gap-3 mb-6 px-6 py-3 bg-orange-100 rounded-full">
          <div className="w-8 h-8 bg-orange-600 rounded-lg flex items-center justify-center">
            <Package className="w-4 h-4 text-white" />
          </div>
          <span className="font-semibold text-orange-700">{industryConfig.name}</span>
        </div>
        
        <h2 className="text-4xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent mb-6 flex items-center justify-center gap-3">
          Supply Chain & Cost Intelligence
          <HelpTooltip text="Monitor your key suppliers and supply chain for cost changes, disruptions, and opportunities. We'll track price trends, supplier stability, and alternative sourcing options to help you make proactive decisions." />
        </h2>
        <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
          Configure monitoring for your key suppliers to predict cost changes, 
          supply disruptions, and identify alternative sourcing opportunities before they impact your operations.
        </p>

        {/* Progress Stats */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4 max-w-2xl mx-auto">
          <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
            <div className="text-2xl font-bold text-gray-900">{stats.configuredCategories}</div>
            <div className="text-sm text-gray-600">Categories Selected</div>
          </div>
          <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
            <div className="text-2xl font-bold text-orange-600">{stats.completedConfigurations}</div>
            <div className="text-sm text-gray-600">Fully Configured</div>
          </div>
          <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
            <div className={cn(
              "text-2xl font-bold",
              stats.completionPercentage >= 80 ? "text-green-600" : 
              stats.completionPercentage >= 50 ? "text-yellow-600" : "text-gray-500"
            )}>
              {stats.completionPercentage}%
            </div>
            <div className="text-sm text-gray-600">Completion</div>
          </div>
        </div>
      </div>

      {/* Supplier Category Selection */}
      <Card className="border-2 hover:border-primary/30 transition-colors duration-300">
        <CardHeader className="pb-6">
          <CardTitle className="flex items-center gap-3 text-xl">
            <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center">
              <Target className="w-5 h-5 text-white" />
            </div>
            Supplier Categories
          </CardTitle>
          <CardDescription className="text-base">
            Select the supplier categories that are most critical to your business operations and cost structure
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
            {industryConfig.supplierCategories.map((category) => {
              const isSelected = data.suppliers.some(s => s.category === category.id)
              const IconComponent = getCategoryIcon(category.name)
              
              return (
                <Card 
                  key={category.id}
                  className={cn(
                    "cursor-pointer transition-all duration-300 hover:shadow-lg border-2 relative overflow-hidden",
                    isSelected 
                      ? "border-orange-500 bg-orange-50 shadow-lg" 
                      : "border-gray-200 hover:border-orange-300"
                  )}
                  onClick={() => {
                    if (isSelected) {
                      removeSupplierCategory(category.id)
                    } else {
                      addSupplierCategory(category.id)
                    }
                  }}
                >
                  {/* Selection indicator */}
                  {isSelected && (
                    <div className="absolute top-4 right-4 z-10">
                      <CheckCircle2 className="w-5 h-5 text-orange-600 fill-orange-100" />
                    </div>
                  )}

                  <CardContent className="p-6">
                    <div className="space-y-4">
                      <div className="flex items-start gap-3">
                        <div className={cn(
                          "w-12 h-12 rounded-xl flex items-center justify-center transition-colors",
                          isSelected 
                            ? "bg-orange-100 text-orange-600" 
                            : "bg-gray-100 text-gray-500"
                        )}>
                          <IconComponent className="w-6 h-6" />
                        </div>
                        <div className="flex-1">
                          <div className="font-semibold text-lg mb-2">{category.name}</div>
                        </div>
                      </div>
                      
                      <div className="space-y-3">
                        <div>
                          <div className="text-sm font-medium text-gray-700 mb-2">Common Suppliers:</div>
                          <div className="space-y-1">
                            {category.suppliers.slice(0, 3).map((supplier, index) => (
                              <div key={index} className="flex items-center text-sm text-gray-600">
                                <div className="w-1.5 h-1.5 bg-orange-400 rounded-full mr-2" />
                                {supplier}
                              </div>
                            ))}
                            {category.suppliers.length > 3 && (
                              <div className="text-sm text-gray-500 italic ml-3.5">
                                +{category.suppliers.length - 3} more options
                              </div>
                            )}
                          </div>
                        </div>

                        <div>
                          <div className="text-sm font-medium text-gray-700 mb-2">Spend Ranges:</div>
                          <div className="flex flex-wrap gap-1">
                            {category.spendRanges.slice(0, 2).map((range, index) => (
                              <span key={index} className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                                {range}
                              </span>
                            ))}
                            {category.spendRanges.length > 2 && (
                              <span className="text-xs text-gray-500">
                                +{category.spendRanges.length - 2} more
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )
            })}
          </div>
        </CardContent>
      </Card>

      {/* Supplier Configuration */}
      {data.suppliers.length > 0 && (
        <Card className="border-2 hover:border-primary/30 transition-colors duration-300">
          <CardHeader className="pb-6">
            <CardTitle className="flex items-center gap-3 text-xl">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center">
                <BarChart3 className="w-5 h-5 text-white" />
              </div>
              Supplier Configuration Details
            </CardTitle>
            <CardDescription className="text-base">
              Configure your primary suppliers, spending levels, and backup options for intelligent monitoring
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-8">
            {data.suppliers.map((supplier, index) => {
              const categoryConfig = industryConfig.supplierCategories.find(c => c.id === supplier.category)
              if (!categoryConfig) return null

              const isComplete = supplier.primarySupplier && supplier.monthlySpend
              const IconComponent = getCategoryIcon(categoryConfig.name)

              return (
                <Card key={supplier.category} className="border-l-4 border-l-orange-500 shadow-sm">
                  <CardContent className="p-8">
                    <div className="flex items-center justify-between mb-6">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
                          <span className="text-orange-600 font-bold text-sm">{index + 1}</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <IconComponent className="w-5 h-5 text-orange-600" />
                          <h4 className="font-semibold text-xl">{categoryConfig.name}</h4>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {isComplete && (
                          <CheckCircle2 className="w-5 h-5 text-green-600" />
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeSupplierCategory(supplier.category)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                      <div className="space-y-6">
                        <div className="space-y-3">
                          <Label htmlFor={`primary-${supplier.category}`} className="flex items-center gap-2 font-medium">
                            <Building2 className="w-4 h-4 text-gray-500" />
                            Primary Supplier
                          </Label>
                          <Select 
                            value={supplier.primarySupplier} 
                            onValueChange={(value) => updateSupplier(supplier.category, 'primarySupplier', value)}
                          >
                            <SelectTrigger className="h-12 text-base">
                              <SelectValue placeholder="Select your primary supplier" />
                            </SelectTrigger>
                            <SelectContent>
                              {categoryConfig.suppliers.map((supplierOption) => (
                                <SelectItem key={supplierOption} value={supplierOption} className="py-3">
                                  {supplierOption}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        <div className="space-y-3">
                          <Label htmlFor={`spend-${supplier.category}`} className="flex items-center gap-2 font-medium">
                            <DollarSign className="w-4 h-4 text-gray-500" />
                            Monthly Spend Range
                          </Label>
                          <Select 
                            value={supplier.monthlySpend} 
                            onValueChange={(value) => updateSupplier(supplier.category, 'monthlySpend', value)}
                          >
                            <SelectTrigger className="h-12 text-base">
                              <SelectValue placeholder="Select your spending range" />
                            </SelectTrigger>
                            <SelectContent>
                              {categoryConfig.spendRanges.map((range) => (
                                <SelectItem key={range} value={range} className="py-3">
                                  {range}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        {supplier.primarySupplier && supplier.monthlySpend && (
                          <div className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl border border-green-200">
                            <div className="flex items-start gap-3">
                              <Shield className="w-5 h-5 text-green-600 mt-0.5" />
                              <div>
                                <div className="font-semibold text-green-800 mb-1">Monitoring Active</div>
                                <div className="text-sm text-green-700">
                                  We'll track <strong>{supplier.primarySupplier}</strong> for price changes, 
                                  supply issues, and market developments in your spending range.
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>

                      <div className="space-y-4">
                        <Label htmlFor={`alternatives-${supplier.category}`} className="flex items-center gap-2 font-medium">
                          <Package className="w-4 h-4 text-gray-500" />
                          Alternative Suppliers & Backup Options
                        </Label>
                        <Textarea
                          id={`alternatives-${supplier.category}`}
                          value={supplier.alternativeSuppliers?.join(', ') || ''}
                          onChange={(e) => {
                            const alternatives = e.target.value.split(',').map(s => s.trim()).filter(s => s)
                            updateSupplier(supplier.category, 'alternativeSuppliers', alternatives)
                          }}
                          placeholder="List your backup suppliers, alternative sources, or other options you've considered (comma-separated)..."
                          rows={6}
                          className="text-base"
                        />
                        {supplier.alternativeSuppliers && supplier.alternativeSuppliers.length > 0 && (
                          <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                            <div className="text-sm text-blue-700">
                              <strong>Smart Backup Strategy:</strong> We'll monitor your alternatives and alert you 
                              when better pricing or availability becomes available.
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )
            })}
          </CardContent>
        </Card>
      )}

      {/* Intelligence Preview */}
      {data.suppliers.length > 0 && (
        <div className="p-8 bg-gradient-to-br from-orange-50 to-amber-50 border border-orange-200 rounded-2xl">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-orange-600" />
            </div>
            <div className="flex-1">
              <h3 className="text-xl font-bold text-orange-800 mb-3">Supply Chain Intelligence Preview</h3>
              <p className="text-orange-700 mb-6">
                Our AI will continuously monitor your configured suppliers and provide early warnings about:
              </p>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                <div className="space-y-3">
                  <div className="flex items-center gap-2 font-semibold text-orange-800">
                    <AlertTriangle className="w-5 h-5" />
                    Risk Monitoring
                  </div>
                  <ul className="space-y-2 text-sm text-orange-700 ml-7">
                    <li>• Price increases 30-60 days before implementation</li>
                    <li>• Supply chain disruptions and material shortages</li>
                    <li>• Supplier financial health and stability issues</li>
                    <li>• Market volatility affecting your categories</li>
                  </ul>
                </div>
                
                <div className="space-y-3">
                  <div className="flex items-center gap-2 font-semibold text-orange-800">
                    <Target className="w-5 h-5" />
                    Opportunity Alerts
                  </div>
                  <ul className="space-y-2 text-sm text-orange-700 ml-7">
                    <li>• Better pricing from alternative suppliers</li>
                    <li>• New suppliers entering your market</li>
                    <li>• Bulk purchase opportunities and discounts</li>
                    <li>• Market trends favoring cost reductions</li>
                  </ul>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {data.suppliers.filter(s => s.primarySupplier).map((supplier) => {
                  const categoryConfig = industryConfig.supplierCategories.find(c => c.id === supplier.category)
                  return (
                    <div key={supplier.category} className="bg-white/70 rounded-xl p-4 border border-orange-200/50">
                      <div className="flex items-start gap-3">
                        <Package className="w-4 h-4 text-orange-700 mt-0.5 flex-shrink-0" />
                        <div>
                          <div className="font-semibold text-orange-800 text-sm">
                            {categoryConfig?.name}
                          </div>
                          <div className="text-xs text-orange-700 mt-1">
                            Monitoring: {supplier.primarySupplier}
                          </div>
                          <div className="text-xs text-orange-600 mt-1">
                            Spend: {supplier.monthlySpend}
                          </div>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
              
              <div className="mt-6 p-4 bg-white/50 rounded-xl border border-orange-200/50">
                <div className="text-sm text-orange-700 font-medium">
                  🚀 Next: Configure competitive intelligence to complete your strategic monitoring setup.
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Completion Status */}
      {stats.configuredCategories > 0 && (
        <div className={cn(
          "p-6 rounded-xl border-2 transition-all duration-300",
          stats.completionPercentage >= 80 ? "bg-green-50 border-green-200" :
          stats.completionPercentage >= 50 ? "bg-yellow-50 border-yellow-200" :
          "bg-gray-50 border-gray-200"
        )}>
          <div className="flex items-center gap-4">
            <div className={cn(
              "w-12 h-12 rounded-xl flex items-center justify-center",
              stats.completionPercentage >= 80 ? "bg-green-100" :
              stats.completionPercentage >= 50 ? "bg-yellow-100" :
              "bg-gray-100"
            )}>
              {stats.completionPercentage >= 80 ? (
                <CheckCircle2 className="w-6 h-6 text-green-600" />
              ) : (
                <BarChart3 className="w-6 h-6 text-gray-600" />
              )}
            </div>
            <div>
              <div className="text-lg font-semibold mb-1">
                Supply Chain Configuration: {stats.completionPercentage}% Complete
              </div>
              <div className="text-sm text-gray-600">
                {stats.completedConfigurations} of {stats.configuredCategories} categories fully configured
                {stats.completionPercentage >= 80 && " - Ready for next step!"}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}